import { FormattedMessage, useIntl } from 'react-intl';
import {
  ValuationTitle,
  ValuationPricesContainer,
  ValuationPricesTopRow,
  ValuationPricesMain,
  ValuationMainPriceContainer,
  ValuationMainPrice,
  ValuationMainPriceSufix,
  ValuationSqmPrice,
  ValuationPricesBreakdown,
  ValuationPricesBreakdownPartLabel,
  ValuationBreakdownDotMax,
  ValuationBreakdownDotMin,
  ValuationSeparator,
  ValuationMarketSituationContainer,
  ValuationMarketSituationTitle,
  ValuationMarketSituationDataBlocks,
  ValuationMarketSituationDataBlock,
  ValuationMarketSituationDataBlockLabel,
  ValuationMarketSituationDataBlockValueContainer,
  ValuationMarketSituationDataBlockValue,
  ValuationMarketSituationDataBlockValueSufix,
  ValuationContainer,
  ValuationStatusPrice,
  ValuationStatusPriceLabel,
  ValuationStatusPriceValue,
  Separator,
  ValuationPricesBreakdownPartMin,
  ValuationPricesBreakdownPartMax,
  ValuationMarketSituationContent,
  FeatureCell,
} from './valuationSubmodules.styles';

import { getMethodCellColor } from '../valuation.module';
import { formatCurrencyExtended } from '../../../../helpers/currencyFormatter';
import { RentProfile } from '../../../../types/valuation.interface';
import { SocialFinancialCards } from '../../ReportFinancial/SocialFinancialCards';
import { SocialFinancialDataModel } from '../../../../types/indicators.interfaces';
import { NoDataContainer } from '../../NoDataComponent/noData.styles';
import NoDataIcon from '../../../../assets/images/error.png';
import { isLatamCountry } from '../../../../helpers/countries';

export function RentValuation({
  isExcluded,
  data,
  reportType,
  operation,
  listingType,
  social_financial,
  country = 'es',
  otherOperationFailed,
}: {
  isExcluded: boolean;
  data: RentProfile;
  reportType: number;
  estimatedMin: number | null;
  estimatedMax: number | null;
  operation: number;
  listingType?: number;
  social_financial?: SocialFinancialDataModel[] | null;
  country?: string;
  otherOperationFailed?: boolean;
}) {
  const intl = useIntl();

  // Check if we should show social financial cards instead of market situation
  const shouldShowSocialFinancial =
    (reportType === 2 || reportType === 3) &&
    listingType &&
    (listingType === 8 || listingType === 12);

  // En LATAM no se muestra el bloque de situación de mercado. Se deja su hueco en la rejilla
  // (60% / 40%) para no descuadrar la página del informe.
  const isLatam = isLatamCountry(country);

  const personalisedReport = reportType === 2 && operation === 0;

  // Get translated suffixes
  const monthSuffix = intl.formatMessage({ id: 'rentPriceSuffix' });
  const sqmSuffix = intl.formatMessage({ id: 'sqmPriceSuffix' });

  return (
    <ValuationContainer isexcluded={isExcluded.toString()}>
      <ValuationPricesContainer
        aria-label="valuation-prices-container"
        pricesBreakdown={data.priceRange === null && !shouldShowSocialFinancial}
      >
        <ValuationPricesTopRow>
        <ValuationPricesMain aria-label="valuation-prices-main">
          <ValuationTitle>
            <FormattedMessage id="valuation.rentPriceTitle" />
          </ValuationTitle>
          {otherOperationFailed ? (
            <NoDataContainer>
              <img src={NoDataIcon} />
              Valuation not provided due to lack of data, you can exclude this field in configure modules settings ⚙️ 
            </NoDataContainer>
          ) : (
            <ValuationMainPriceContainer>
              <ValuationMainPrice
                operation={0}
                pricesBreakdown={data.priceRange !== null && !personalisedReport}
              >
                {data.mainValue ? (
                  <>
                    {formatCurrencyExtended(
                      data.mainValue,
                      country,
                      false,
                      sqmSuffix,
                      monthSuffix,
                      true,
                      1000000000,
                      false,
                      true
                    )}
                    <ValuationMainPriceSufix
                      operation={0}
                      pricesBreakdown={
                        data.priceRange !== null && !personalisedReport
                      }
                    >
                      {formatCurrencyExtended(
                        data.mainValue,
                        country,
                        false,
                        sqmSuffix,
                        undefined,
                        false,
                        1000000000,
                        true
                      )}
                    </ValuationMainPriceSufix>
                  </>
                ) : (
                  '- '
                )}
              </ValuationMainPrice>
              <ValuationSqmPrice>
                {data.sqmPrice ? (
                  formatCurrencyExtended(
                    data.sqmPrice,
                    country,
                    true,
                    sqmSuffix,
                    undefined,
                    false,
                    1000000
                  )
                ) : (
                  '- '
                )}
              </ValuationSqmPrice>
            </ValuationMainPriceContainer>
          )}
          {(() => {
            const colors = data.bestMethod
              ? getMethodCellColor(data.bestMethod)
              : null;
            return colors?.backgroundcolor &&
              colors?.color &&
              (reportType !== 2 ||
                ((reportType === 2 || reportType === 3) && operation === 1)) ? (
              <FeatureCell
                backgroundcolor={colors.backgroundcolor}
                color={colors.color}
              >
                <FormattedMessage id={`valuation.${data.bestMethod}`} />
              </FeatureCell>
            ) : null;
          })()}
          {data?.priceRange !== null && (
            <ValuationPricesBreakdown>
              <ValuationPricesBreakdownPartMin>
                <ValuationPricesBreakdownPartLabel>
                  <ValuationBreakdownDotMin />
                  <FormattedMessage id="min" />
                </ValuationPricesBreakdownPartLabel>
                <span>
                  {data.priceRange && data.priceRange[0]
                    ? personalisedReport
                      ? (() => {
                          const factor = data.mainValue * 0.1;
                          const valor = data.mainValue - factor;
                          return formatCurrencyExtended(
                            valor,
                            country,
                            false,
                            sqmSuffix,
                            undefined,
                            true,
                            1000000
                          );
                        })()
                      : formatCurrencyExtended(
                          data.priceRange[0],
                          country,
                          false,
                          sqmSuffix,
                          undefined,
                          true,
                          1000000
                        )
                    : '- '}
                </span>
              </ValuationPricesBreakdownPartMin>
              <ValuationPricesBreakdownPartMax>
                <ValuationPricesBreakdownPartLabel>
                  <ValuationBreakdownDotMax />
                  <FormattedMessage id="max" />
                </ValuationPricesBreakdownPartLabel>
                <span>
                  {data.priceRange && data.priceRange[1]
                    ? personalisedReport
                      ? (() => {
                          const factor = data.mainValue * 0.1;
                          const valor = data.mainValue + factor;
                          return formatCurrencyExtended(
                            valor,
                            country,
                            false,
                            sqmSuffix,
                            undefined,
                            true,
                            1000000
                          );
                        })()
                      : formatCurrencyExtended(
                          data.priceRange[1],
                          country,
                          false,
                          sqmSuffix,
                          undefined,
                          true,
                          1000000
                        )
                    : '- '}
                </span>
              </ValuationPricesBreakdownPartMax>
            </ValuationPricesBreakdown>
          )}
        </ValuationPricesMain>
        <ValuationSeparator
          aria-label="valaution-separator"
          show={data?.priceByStatus}
        >
          {data?.priceByStatus && (
            <>
              <ValuationStatusPrice>
                <ValuationStatusPriceLabel>
                  <FormattedMessage id="buildStatus.6" />
                </ValuationStatusPriceLabel>
                <ValuationStatusPriceValue>
                  {data.priceByStatus && data.priceByStatus.price_6
                    ? personalisedReport
                      ? (() => {
                          const value = data.mainValue * 1.1125;
                          return formatCurrencyExtended(
                            value,
                            country,
                            false,
                            sqmSuffix,
                            undefined,
                            true,
                            1000000
                          );
                        })()
                      : formatCurrencyExtended(
                          data.priceByStatus.price_6,
                          country,
                          false,
                          sqmSuffix,
                          undefined,
                          true,
                          1000000
                        )
                    : '- '}
                </ValuationStatusPriceValue>
              </ValuationStatusPrice>
              <ValuationStatusPrice>
                <ValuationStatusPriceLabel>
                  <FormattedMessage id="buildStatus.1" />
                </ValuationStatusPriceLabel>
                <ValuationStatusPriceValue>
                  {data.priceByStatus && data.priceByStatus.price_1
                    ? personalisedReport
                      ? (() => {
                          const value = data.mainValue;
                          return formatCurrencyExtended(
                            value,
                            country,
                            false,
                            sqmSuffix,
                            undefined,
                            true,
                            1000000
                          );
                        })()
                      : formatCurrencyExtended(
                          data.priceByStatus.price_1,
                          country,
                          false,
                          sqmSuffix,
                          undefined,
                          true,
                          1000000
                        )
                    : '- '}
                </ValuationStatusPriceValue>
              </ValuationStatusPrice>
              <ValuationStatusPrice>
                <ValuationStatusPriceLabel>
                  <FormattedMessage id="buildStatus.2" />
                </ValuationStatusPriceLabel>
                <ValuationStatusPriceValue>
                  {data.priceByStatus && data.priceByStatus.price_2
                    ? personalisedReport
                      ? (() => {
                          const value = data.mainValue * 0.925;
                          return formatCurrencyExtended(
                            value,
                            country,
                            false,
                            sqmSuffix,
                            undefined,
                            true,
                            1000000
                          );
                        })()
                      : formatCurrencyExtended(
                          data.priceByStatus.price_2,
                          country,
                          false,
                          sqmSuffix,
                          undefined,
                          true,
                          1000000
                        )
                    : '- '}
                </ValuationStatusPriceValue>
              </ValuationStatusPrice>
              <ValuationStatusPrice>
                <ValuationStatusPriceLabel>
                  <FormattedMessage id="buildStatus.5" />
                </ValuationStatusPriceLabel>
                <ValuationStatusPriceValue>
                  {data.priceByStatus && data.priceByStatus.price_5
                    ? personalisedReport
                      ? (() => {
                          const value = data.mainValue * 0.85;
                          return formatCurrencyExtended(
                            value,
                            country,
                            false,
                            sqmSuffix,
                            undefined,
                            true,
                            1000000
                          );
                        })()
                      : formatCurrencyExtended(
                          data.priceByStatus.price_5,
                          country,
                          false,
                          sqmSuffix,
                          undefined,
                          true,
                          1000000
                        )
                    : '- '}
                </ValuationStatusPriceValue>
              </ValuationStatusPrice>
            </>
          )}
        </ValuationSeparator>
        </ValuationPricesTopRow>
      </ValuationPricesContainer>
      {shouldShowSocialFinancial ? (
        <div style={{ marginTop: '-70px' }}>
          <SocialFinancialCards
            social_financial={social_financial || null}
            country={country}
          />
        </div>
      ) : (
        !isLatam && (
          <ValuationMarketSituationContainer>
            <ValuationMarketSituationContent>
              <ValuationMarketSituationTitle>
                <FormattedMessage id="valuation.rentMarketSituation" />
              </ValuationMarketSituationTitle>
              <ValuationMarketSituationDataBlocks>
                <ValuationMarketSituationDataBlock operation={0}>
                  <ValuationMarketSituationDataBlockLabel>
                    <FormattedMessage id="neighborhood" />
                  </ValuationMarketSituationDataBlockLabel>
                  <ValuationMarketSituationDataBlockValueContainer>
                    <ValuationMarketSituationDataBlockValue>
                      {data.yieldRental.neighborhood
                        ? data.yieldRental.neighborhood
                        : '-'}
                    </ValuationMarketSituationDataBlockValue>
                    <ValuationMarketSituationDataBlockValueSufix>
                      %
                    </ValuationMarketSituationDataBlockValueSufix>
                  </ValuationMarketSituationDataBlockValueContainer>
                </ValuationMarketSituationDataBlock>
                <ValuationMarketSituationDataBlock operation={0}>
                  <ValuationMarketSituationDataBlockLabel>
                    <FormattedMessage id="district" />
                  </ValuationMarketSituationDataBlockLabel>
                  <ValuationMarketSituationDataBlockValueContainer>
                    <ValuationMarketSituationDataBlockValue>
                      {data.yieldRental.district
                        ? data.yieldRental.district
                        : '-'}
                    </ValuationMarketSituationDataBlockValue>
                    <ValuationMarketSituationDataBlockValueSufix>
                      %
                    </ValuationMarketSituationDataBlockValueSufix>
                  </ValuationMarketSituationDataBlockValueContainer>
                </ValuationMarketSituationDataBlock>
                <ValuationMarketSituationDataBlock operation={0}>
                  <ValuationMarketSituationDataBlockLabel>
                    <FormattedMessage id="municipality" />
                  </ValuationMarketSituationDataBlockLabel>
                  <ValuationMarketSituationDataBlockValueContainer>
                    <ValuationMarketSituationDataBlockValue>
                      {data.yieldRental.municipality
                        ? data.yieldRental.municipality
                        : '- '}
                    </ValuationMarketSituationDataBlockValue>
                    <ValuationMarketSituationDataBlockValueSufix>
                      %
                    </ValuationMarketSituationDataBlockValueSufix>
                  </ValuationMarketSituationDataBlockValueContainer>
                </ValuationMarketSituationDataBlock>
              </ValuationMarketSituationDataBlocks>
            </ValuationMarketSituationContent>
          </ValuationMarketSituationContainer>
        )
      )}
    </ValuationContainer>
  );
}
